import { useState } from 'react'
import { Modal, Button, Table, Spinner, Badge, Accordion } from 'react-bootstrap'
import { useQuery, useMutation } from '@apollo/client'
import { toast } from 'react-toastify'
import { defineMessages, useIntl } from 'react-intl'
import { GET_TWILIO_SYNC_PREVIEW } from '../../gql/queries/twilio'
import { APPLY_SYNC_TWILIO_TEMPLATES } from '../../gql/mutations/twilio'
import { KTIcon } from '../../../../_metronic/helpers'

const M = defineMessages({
  title: {
    id: 'integrations.twilioSync.title',
    defaultMessage: 'Twilio templates sync (diff preview)',
  },
  querying: {
    id: 'integrations.twilioSync.querying',
    defaultMessage: 'Querying Twilio Content API...',
  },
  fetchError: {id: 'integrations.twilioSync.fetchError', defaultMessage: 'Error fetching data:'},
  retry: {id: 'integrations.twilioSync.retry', defaultMessage: 'Retry'},

  summary: {id: 'integrations.twilioSync.summary', defaultMessage: 'Summary'},
  countNew: {id: 'integrations.twilioSync.count.new', defaultMessage: 'New: {count}'},
  countModified: {
    id: 'integrations.twilioSync.count.modified',
    defaultMessage: 'Modified: {count}',
  },
  countUnchanged: {
    id: 'integrations.twilioSync.count.unchanged',
    defaultMessage: 'Unchanged: {count}',
  },
  countRemote: {
    id: 'integrations.twilioSync.count.remote',
    defaultMessage: 'Total remote: {count}',
  },
  selectedToSave: {
    id: 'integrations.twilioSync.selectedToSave',
    defaultMessage: 'Selected to save: {count}',
  },

  newDetected: {
    id: 'integrations.twilioSync.newDetected',
    defaultMessage: 'New templates detected',
  },
  colSidName: {id: 'integrations.twilioSync.col.sidName', defaultMessage: 'SID / Name'},
  colAction: {id: 'common.field.action', defaultMessage: 'Action'},
  willBeCreated: {
    id: 'integrations.twilioSync.willBeCreated',
    defaultMessage: 'Will be created locally',
  },

  withDifferences: {
    id: 'integrations.twilioSync.withDifferences',
    defaultMessage: 'Templates with differences',
  },
  modifiedFields: {
    id: 'integrations.twilioSync.modifiedFields',
    defaultMessage: 'Modified fields: {count}',
  },
  colLocalVersion: {
    id: 'integrations.twilioSync.col.localVersion',
    defaultMessage: 'Current local version',
  },
  colRemoteVersion: {
    id: 'integrations.twilioSync.col.remoteVersion',
    defaultMessage: 'New version (Twilio)',
  },
  replaceHint: {
    id: 'integrations.twilioSync.replaceHint',
    defaultMessage: "By saving, the local version will be replaced with Twilio's data.",
  },

  upToDateTitle: {
    id: 'integrations.twilioSync.upToDateTitle',
    defaultMessage: 'Everything is up to date!',
  },
  upToDateHint: {
    id: 'integrations.twilioSync.upToDateHint',
    defaultMessage:
      'There are no differences between Twilio templates and your local database.',
  },

  saving: {
    id: 'integrations.twilioSync.saving',
    defaultMessage: '{count, plural, one {Saving # template...} other {Saving # templates...}}',
  },
  saveSelected: {
    id: 'integrations.twilioSync.saveSelected',
    defaultMessage:
      '{count, plural, one {Save (#) selected} other {Save (#) selected}}',
  },

  toastSelectOne: {
    id: 'integrations.twilioSync.toast.selectOne',
    defaultMessage: 'You must select at least one template to sync.',
  },
  toastApplyFailed: {
    id: 'integrations.twilioSync.toast.applyFailed',
    defaultMessage: 'Failed to apply sync',
  },
  toastApplyError: {
    id: 'integrations.twilioSync.toast.applyError',
    defaultMessage: 'Critical error applying sync',
  },

  // Reusadas
  status: {id: 'common.field.status', defaultMessage: 'Status'},
  language: {id: 'comms.templates.variant.waLanguage', defaultMessage: 'Language'},
  field: {id: 'leads.activity.field', defaultMessage: 'Field'},
  cancel: {id: 'common.action.cancel', defaultMessage: 'Cancel'},
})

export const TwilioSyncDiffModal = ({
  show,
  onHide,
  onSuccess
}: {
  show: boolean
  onHide: () => void
  onSuccess: () => void
}) => {
  const intl = useIntl()
  const [selectedSids, setSelectedSids] = useState<Set<string>>(new Set())

  // Fetch the diff (preview sync without saving)
  const { data, loading, error, refetch } = useQuery(GET_TWILIO_SYNC_PREVIEW, {
    skip: !show,
    fetchPolicy: 'network-only',
    onCompleted: (d) => {
      // Auto-select all new and changed templates by default
      const sids = new Set<string>()
      d.twilioSyncPreview?.newTemplates.forEach((t: any) => sids.add(t.contentSid))
      d.twilioSyncPreview?.changedTemplates.forEach((t: any) => sids.add(t.contentSid))
      setSelectedSids(sids)
    }
  })

  // Apply the sync
  const [applySync, { loading: applying }] = useMutation(APPLY_SYNC_TWILIO_TEMPLATES)

  const diff = data?.twilioSyncPreview

  const handleToggleSelect = (sid: string) => {
    const next = new Set(selectedSids)
    if (next.has(sid)) next.delete(sid)
    else next.add(sid)
    setSelectedSids(next)
  }

  const handleApply = async () => {
    if (selectedSids.size === 0) {
      toast.warning(intl.formatMessage(M.toastSelectOne))
      return
    }

    try {
      const { data: res } = await applySync({
        variables: { acceptedSids: Array.from(selectedSids) }
      })

      if (res?.applySyncTwilioTemplates?.success) {
        toast.success(res.applySyncTwilioTemplates.message)
        onSuccess()
        onHide()
      } else {
        toast.error(
          res?.applySyncTwilioTemplates?.message || intl.formatMessage(M.toastApplyFailed)
        )
      }
    } catch (e: any) {
      toast.error(e.message || intl.formatMessage(M.toastApplyError))
    }
  }

  return (
    <Modal show={show} onHide={onHide} size="xl" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>{intl.formatMessage(M.title)}</Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" />
            <div className="mt-2 text-muted">{intl.formatMessage(M.querying)}</div>
          </div>
        ) : error ? (
          <div className="alert alert-danger">
            <strong>{intl.formatMessage(M.fetchError)}</strong> {error.message}
            <div className="mt-3">
              <Button size="sm" variant="danger" onClick={() => refetch()}>
                {intl.formatMessage(M.retry)}
              </Button>
            </div>
          </div>
        ) : diff ? (
          <div>
            <div className="d-flex justify-content-between mb-4">
              <div>
                <h5>{intl.formatMessage(M.summary)}</h5>
                <span className="badge badge-light-success fw-bolder px-4 py-3 me-2">
                  {intl.formatMessage(M.countNew, {count: diff.newTemplates.length})}
                </span>
                <span className="badge badge-light-warning fw-bolder px-4 py-3 me-2">
                  {intl.formatMessage(M.countModified, {count: diff.changedTemplates.length})}
                </span>
                <span className="badge badge-light-secondary fw-bolder px-4 py-3 me-2">
                  {intl.formatMessage(M.countUnchanged, {count: diff.unchangedCount})}
                </span>
                <span className="badge badge-light-primary fw-bolder px-4 py-3">
                  {intl.formatMessage(M.countRemote, {count: diff.totalRemote})}
                </span>
              </div>
              <div className="text-end text-muted small pt-4">
                {intl.formatMessage(M.selectedToSave, {count: selectedSids.size})}
              </div>
            </div>

            {/* Nuevas Plantillas */}
            {diff.newTemplates.length > 0 && (
              <div className="mb-5">
                <h6 className="text-success fw-bolder mb-3">
                  <KTIcon iconName="plus" className="fs-3 text-success me-1" />
                  {intl.formatMessage(M.newDetected)}
                </h6>
                <Table hover responsive className="align-middle border rounded">
                  <thead className="table-light">
                    <tr>
                      <th className="w-50px text-center">
                        <i className="bi bi-check2-square text-muted"></i>
                      </th>
                      <th>{intl.formatMessage(M.colSidName)}</th>
                      <th>{intl.formatMessage(M.status)}</th>
                      <th>{intl.formatMessage(M.language)}</th>
                      <th>{intl.formatMessage(M.colAction)}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {diff.newTemplates.map((t: any) => (
                      <tr key={`new-${t.contentSid}`}>
                        <td className="text-center">
                          <input 
                            className="form-check-input" 
                            type="checkbox" 
                            checked={selectedSids.has(t.contentSid)}
                            onChange={() => handleToggleSelect(t.contentSid)}
                          />
                        </td>
                        <td>
                          <div className="fw-bolder">{t.friendlyName}</div>
                          <div className="text-muted small"><code>{t.contentSid}</code></div>
                        </td>
                        <td><Badge bg={t.approvalStatus === 'approved' ? 'success' : 'warning'}>{t.approvalStatus}</Badge></td>
                        <td>{t.language}</td>
                        <td>
                          <span className="badge badge-light-success">
                            {intl.formatMessage(M.willBeCreated)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}

            {/* Plantillas Modificadas */}
            {diff.changedTemplates.length > 0 && (
              <div className="mb-5">
                <h6 className="text-warning fw-bolder mb-3">
                  <KTIcon iconName="information-5" className="fs-3 text-warning me-1" />
                  {intl.formatMessage(M.withDifferences)}
                </h6>
                <Accordion>
                  {diff.changedTemplates.map((t: any, index: number) => {
                    const local = t.localSnapshot
                    const remote = t.remoteSnapshot
                    return (
                      <Accordion.Item eventKey={index.toString()} key={`changed-${t.contentSid}`}>
                        <Accordion.Header>
                          <div className="d-flex align-items-center w-100">
                            <input 
                                className="form-check-input me-3" 
                                type="checkbox" 
                                checked={selectedSids.has(t.contentSid)}
                                onChange={(e) => {
                                  e.stopPropagation()
                                  handleToggleSelect(t.contentSid)
                                }}
                            />
                            <div className="flex-grow-1">
                              <span className="fw-bolder me-2">{t.friendlyName}</span>
                              <span className="badge badge-light-danger me-2">
                                {intl.formatMessage(M.modifiedFields, {
                                  count: t.diffFields.length,
                                })}
                              </span>
                              <code className="text-muted small">{t.contentSid}</code>
                            </div>
                          </div>
                        </Accordion.Header>
                        <Accordion.Body>
                          <Table bordered size="sm" className="mb-0">
                            <thead className="table-light">
                              <tr>
                                <th>{intl.formatMessage(M.field)}</th>
                                <th className="text-danger">
                                  {intl.formatMessage(M.colLocalVersion)}
                                </th>
                                <th className="text-success">
                                  {intl.formatMessage(M.colRemoteVersion)}
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {t.diffFields.map((field: string) => (
                                <tr key={field}>
                                  <td className="fw-bold">{field}</td>
                                  <td className="text-danger">
                                    <pre className="mb-0" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '0.8rem' }}>
                                      {typeof local[field] === 'object' ? JSON.stringify(local[field], null, 2) : String(local[field] ?? 'null')}
                                    </pre>
                                  </td>
                                  <td className="text-success">
                                    <pre className="mb-0" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '0.8rem' }}>
                                      {typeof remote[field] === 'object' ? JSON.stringify(remote[field], null, 2) : String(remote[field] ?? 'null')}
                                    </pre>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                          <div className="mt-3 text-muted small">
                            <i className="bi bi-info-circle me-1"></i>
                            {intl.formatMessage(M.replaceHint)}
                          </div>
                        </Accordion.Body>
                      </Accordion.Item>
                    )
                  })}
                </Accordion>
              </div>
            )}

            {diff.newTemplates.length === 0 && diff.changedTemplates.length === 0 && (
              <div className="text-center py-5">
                <i className="bi bi-check-circle text-success fs-1 mb-3 d-block"></i>
                <h5>{intl.formatMessage(M.upToDateTitle)}</h5>
                <p className="text-muted">{intl.formatMessage(M.upToDateHint)}</p>
              </div>
            )}

          </div>
        ) : null}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={applying}>
          {intl.formatMessage(M.cancel)}
        </Button>
        <Button 
          variant="primary" 
          onClick={handleApply} 
          disabled={loading || applying || selectedSids.size === 0 || !diff}
        >
          {applying ? (
            <span className="indicator-progress" style={{ display: 'block' }}>
              {intl.formatMessage(M.saving, {count: selectedSids.size})} <Spinner animation="border" size="sm" className="ms-2 align-middle" />
            </span>
          ) : (
            intl.formatMessage(M.saveSelected, {count: selectedSids.size})
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
